import { z } from 'zod';

// ── Exercise schema ──────────────────────────────────────────────
export const ExerciseSchema = z.object({
  name:       z.string().min(1, 'Exercise name required'),
  sets:       z.number().int().positive().nullable().default(null),
  targetReps: z.union([z.number(), z.string(), z.null()]).default(null),
  notes:      z.string().default(''),
});

// ── Constants ────────────────────────────────────────────────────
export const SCORE_TYPES = ['time', 'rounds', 'reps', 'weight'];
export const CATEGORIES = [
  'EMOM', 'AMRAP', 'ForTime', 'HIIT',
  'Strength', 'Endurance', 'Chipper', 'Other',
];

// ── Base fields shared by every category ─────────────────────────
const baseFields = {
  name:        z.string().min(1, 'Workout name required').max(100),
  description: z.string().max(1000).default(''),
  category:    z.enum(CATEGORIES),
  scoreType:   z.enum(SCORE_TYPES),
  exercises:   z.array(ExerciseSchema).default([]),
  popularity:  z.number().int().min(0).default(0),
  isDeathBy:   z.boolean().default(false),
  thumbUrl:    z.string().nullable().default(null),
};

// ── Per-category schemas ─────────────────────────────────────────

export const EMOMSchema = z.object({
  ...baseFields,
  category:    z.literal('EMOM'),
  scoreType:   z.enum(['rounds', 'time']),
  rounds:      z.number().int().positive().nullable().default(null),
  intervalSec: z.number().positive('EMOM requires interval'),
});

export const AMRAPSchema = z.object({
  ...baseFields,
  category:  z.literal('AMRAP'),
  scoreType: z.literal('rounds'),
  capSec:    z.number().positive('AMRAP requires time cap'),
});

export const ForTimeSchema = z.object({
  ...baseFields,
  category:  z.literal('ForTime'),
  scoreType: z.literal('time'),
  rounds:    z.number().int().positive().nullable().default(null),
  capSec:    z.number().positive().nullable().default(null),
});

export const HIITSchema = z.object({
  ...baseFields,
  category:  z.literal('HIIT'),
  scoreType: z.literal('reps'),
  rounds:    z.number().int().positive('HIIT requires rounds'),
  workSec:   z.number().positive('HIIT requires work time'),
  restSec:   z.number().positive('HIIT requires rest time'),
});

export const StrengthSchema = z.object({
  ...baseFields,
  category:  z.literal('Strength'),
  scoreType: z.literal('weight'),
});

export const EnduranceSchema = z.object({
  ...baseFields,
  category:  z.literal('Endurance'),
  scoreType: z.literal('time'),
  capSec:    z.number().positive().nullable().default(null),
});

export const ChipperSchema = z.object({
  ...baseFields,
  category:  z.literal('Chipper'),
  scoreType: z.literal('time'),
  capSec:    z.number().positive().nullable().default(null),
});

export const OtherSchema = z.object({
  ...baseFields,
  category:  z.literal('Other'),
  scoreType: z.enum(SCORE_TYPES),
  capSec:    z.number().positive().nullable().default(null),
});

// ── Discriminated union ──────────────────────────────────────────
export const WorkoutSchema = z.discriminatedUnion('category', [
  EMOMSchema,
  AMRAPSchema,
  ForTimeSchema,
  HIITSchema,
  StrengthSchema,
  EnduranceSchema,
  ChipperSchema,
  OtherSchema,
]);

/**
 * Validate workout data. Returns { success, data, error }.
 */
export const validateWorkout = (raw) => {
  const result = WorkoutSchema.safeParse(raw);
  if (result.success) {
    return { success: true, data: result.data };
  }
  const messages = result.error.issues.map(
    (i) => `${i.path.join('.')}: ${i.message}`
  );
  return { success: false, error: messages.join('; ') };
};

/**
 * Derive display unit from scoreType.
 */
export const unitForScoreType = (scoreType) => {
  const map = { time: 'minutes', rounds: 'rounds', reps: 'reps', weight: 'kg' };
  return map[scoreType] || 'rounds';
};

/**
 * Normalize a legacy Firestore doc (v1 field names) to v2 schema fields.
 * Safe to call on docs that are already v2 — no-ops gracefully.
 */
export const normalizeLegacy = (doc) => {
  const d = { ...doc };

  // Normalize "5x5" style targetReps into sets + targetReps
  if (Array.isArray(d.exercises)) {
    d.exercises = d.exercises.map((ex) => {
      if (typeof ex.targetReps === 'string' && /^\d+x\d+$/i.test(ex.targetReps)) {
        const [sets, reps] = ex.targetReps.split(/x/i).map(Number);
        return { ...ex, sets, targetReps: reps };
      }
      return ex;
    });
  }

  // workoutCategory -> category ('For Time' -> 'ForTime', 'Tabata' -> 'HIIT')
  if (d.workoutCategory && !d.category) {
    let cat = d.workoutCategory;
    if (cat === 'For Time') cat = 'ForTime';
    if (cat === 'Tabata') cat = 'HIIT';
    d.category = cat;
  }
  if (d.category === 'Tabata') d.category = 'HIIT';
  delete d.workoutCategory;

  // measurementType -> scoreType
  if (d.measurementType && !d.scoreType) {
    d.scoreType = d.measurementType;
  }

  // Strip removed fields
  delete d.measurementType;
  delete d.measurementUnit;
  delete d.durationType;
  delete d.source;
  delete d.originalGlobalId;
  delete d.lastPerformedAt;
  delete d.img;
  delete d.imgCredit;

  // imgThumb -> thumbUrl
  if (d.imgThumb && !d.thumbUrl) d.thumbUrl = d.imgThumb;
  delete d.imgThumb;

  // Default isDeathBy
  if (d.isDeathBy == null) d.isDeathBy = false;

  const cat = d.category;

  if (cat === 'EMOM') {
    // duration (min) -> rounds, workTime (sec) -> intervalSec
    if (d.duration != null && d.rounds == null) d.rounds = d.duration;
    if (d.workTime != null && d.intervalSec == null) d.intervalSec = d.workTime;
    if (d.intervalSec == null) d.intervalSec = 60;
    delete d.duration;
    delete d.workTime;
    delete d.restTime;
  }

  if (cat === 'AMRAP') {
    // duration (min) -> capSec (sec)
    if (d.duration != null && d.capSec == null) d.capSec = d.duration * 60;
    delete d.duration;
    delete d.workTime;
    delete d.restTime;
    delete d.rounds;
  }

  if (cat === 'ForTime') {
    if (d.duration != null && d.capSec == null) d.capSec = d.duration * 60;
    delete d.duration;
    delete d.workTime;
    delete d.restTime;
  }

  if (cat === 'HIIT') {
    if (d.workTime != null && d.workSec == null) d.workSec = d.workTime;
    if (d.restTime != null && d.restSec == null) d.restSec = d.restTime;
    delete d.workTime;
    delete d.restTime;
    delete d.duration;
  }

  if (cat === 'Strength') {
    delete d.duration;
    delete d.workTime;
    delete d.restTime;
    delete d.rounds;
  }

  if (['Endurance', 'Chipper', 'Other'].includes(cat)) {
    if (d.duration != null && d.capSec == null) d.capSec = d.duration * 60;
    delete d.duration;
    delete d.workTime;
    delete d.restTime;
    delete d.rounds;
  }

  return d;
};
